import ReactMarkdown from "react-markdown";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import RehypeExternalLinks from "rehype-external-links";
import RemarkGFM from "remark-gfm";

import { shuffle } from "lodash";

import { ArrowElbowDownLeft, ArrowRight, Lightning, Plus, X } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { PageHeader } from "@/components/layout/heading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Prose } from "@/components/ui/prose";
import { ResponsiveMascot } from "@/components/animations/mascot";

import { ChatCard } from "@/components/card/chat";
import { CommunityCard } from "@/components/card/community";
import { ChatComposer, ChatComposerHandle } from "@/components/chat/composer";

import { useMobileMediaQuery } from "@/hooks/use-media-query";
import { useSession } from "@/hooks/use-session";
import { usePostHog } from "@posthog/react";

import { ChatApiFactory } from "@/apis/chat";
import { KnowledgeApiFactory } from "@/apis/knowledge";
import { onDashboardViewedEvent } from "@/events/engagement";

import { mCommunityCreatives } from "@/constants/community-assets";
import { CreativeTool, mCreativeToolsV2 as mCreativeTools } from "@/constants/creative-tools";
import { MockOfferings, MockPrompts } from "@/constants/mock-prompts";

import { processPromptWithProduct } from "@/lib/chat";
import { cn } from "@/lib/utils";

import type { AgentSettings as IAgentSettings, Offering } from "@/types/agent";

const mPageSize = 20;
const mGutterBreakpoints = { 300: 16 };
const mColumnBreakpoints = { 300: 1, 500: 2, 800: 3, 1200: 4 };

export const Route = createFileRoute("/dashboard/_layout/")({
  component: RouteComponent,
});

function RouteComponent() {
  const posthog = usePostHog();

  const session = useSession();
  const mobile = useMobileMediaQuery();

  const composer$ = useRef<ChatComposerHandle>(null);
  const name = session.user.name.split(" ")[0];

  useEffect(() => {
    onDashboardViewedEvent({ name: session.user.name }, { posthog });
  }, [session.user.name, posthog]);

  const handleSelectPrompt = (prompt: string, settings: IAgentSettings) => {
    composer$.current?.setPrompt(prompt);
    composer$.current?.setSettings(settings);
  };

  return (
    <section className="@container flex flex-col md:pt-16">
      <div className="w-full max-w-6xl mx-auto">
        <div className="h-[calc(100dvh-7rem)] md:h-fit w-full relative flex flex-col">
          <div className="flex-1 grid place-items-center">
            <div className="space-y-2 relative z-10">
              <h1 className="text-4xl font-medium tracking-tight text-center font-display">Greetings, {name}</h1>
              <p className="text-sm text-muted-foreground text-center">What would you like to create with Zocket today?</p>
            </div>
          </div>
          <ResponsiveMascot mascot="MascotHi" className="hidden @2xl:block" />
          <ChatComposer ref={composer$} microphone context chatSettings className="md:mt-10" rotatingPlaceholder />
        </div>
        {!mobile ? <CreativeTools onSelectPrompt={handleSelectPrompt} /> : null}
        {!mobile ? <RecentChats /> : null}
        {!mobile ? <CommunityFeed /> : null}
      </div>
    </section>
  );
}

interface CreativeToolsProps {
  onSelectPrompt: (prompt: string, settings: IAgentSettings) => void;
}

function CreativeTools({ onSelectPrompt }: CreativeToolsProps) {
  const client = useQueryClient();
  const navigate = Route.useNavigate();

  const ref = useRef<HTMLDivElement>(null!);
  const [selected, setSelected] = useState<CreativeTool | null>(null);

  const { profile } = useSession();
  const { data: products = [] } = useQuery({
    ...KnowledgeApiFactory.Queries.FetchBrandProducts({ page: 1, size: mPageSize }),
    select: (data) => data.items,
  });

  const { mutateAsync: createChatSession, isPending } = useMutation({
    mutationFn: ChatApiFactory.Apis.CreateChatSession,
  });

  const offerings = useMemo(() => {
    const offerings: Offering[] = [...(profile.company.brand_metadata?.offerings || MockOfferings)];
    if (products.length) offerings.push(...products.map((product) => ({ name: product.name, url: "" })));
    return shuffle(offerings);
  }, [products, profile.company.brand_metadata]);

  useEffect(() => {
    const controller = new AbortController();

    document.addEventListener(
      "mousedown",
      (e) => {
        const target = e.target as Node;
        if (!ref.current || ref.current.contains(target)) return;
        setSelected(null);
      },
      {
        signal: controller.signal,
      },
    );

    return () => {
      controller.abort();
    };
  }, []);

  if (selected) {
    return (
      <div className="flex flex-col gap-px rounded-2xl overflow-hidden mt-8 border" ref={ref}>
        <div className="w-full bg-card p-4 border-b">
          <div className="flex items-center justify-start gap-4">
            <div className={cn("rounded-md size-10 aspect-square grid place-items-center shrink-0", selected.colors.background, selected.colors.text)}>
              <selected.Icon weight="regular" className="size-6" />
            </div>
            <div className="flex flex-col gap-0.5">
              <p className="font-semibold text-sm">{selected.title}</p>
              <p className="text-sm text-muted-foreground line-clamp-1">{selected.description}</p>
            </div>
            <Button size="icon" variant="ghost" className="ml-auto" onClick={() => setSelected(null)}>
              <X weight="bold" className="size-4" />
            </Button>
          </div>
        </div>
        <div className="flex flex-col divide-y -mt-px">
          {MockPrompts[selected.id].map((prompt, index) => {
            return <MockPrompt key={index} prompt={prompt.prompt} settings={prompt.settings} offerings={offerings} onSelectPrompt={onSelectPrompt} />;
          })}
        </div>
      </div>
    );
  }

  const handleSelect = (tool: CreativeTool) => {
    if (!isPending) {
      const body = {
        agent: tool.agent,
        settings: {
          outputFormat: tool.outputFormat,
        },
      };
      createChatSession(body, {
        onSuccess: (response) => {
          navigate({ to: "/dashboard/chat/$session", params: { session: response.id }, search: { tool: tool.id } });
          client.invalidateQueries({ predicate: (query) => query.queryKey[0] === "chat" });
        },
      });
    }
  };

  return (
    <div className="w-full mt-8">
      <div className="grid grid-cols-1 @2xl:grid-cols-2 gap-4">
        {mCreativeTools.map((tool) => (
          <button
            key={tool.id}
            onClick={() => handleSelect(tool)}
            className="group rounded-xl text-left flex items-center gap-4 bg-card transition-all not-disabled:hover:shadow-2xl shadow-gray-400/15 outline-none border p-4 pr-6 cursor-pointer relative"
          >
            <div className={cn("rounded-md size-12 aspect-square flex items-center justify-center overflow-hidden shrink-0", tool.colors.background, tool.colors.text)}>
              <tool.Icon weight="regular" className="size-6" />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-start">
                <p className="font-semibold text-sm">{tool.title}</p>
                <span className="text-xs text-muted-foreground mx-2">•</span>
                <div className="flex items-center gap-0.5 -ml-0.5">
                  <Lightning weight="fill" className="size-3 fill-yellow-600" />
                  <span className="text-xs text-foreground font-medium">
                    {tool.creditsLow} - {tool.creditsHigh} Credits
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">{tool.subtitle}</p>
            </div>
            <div className="absolute top-2 right-2 flex items-center justify-center">
              {tool.recommended ? (
                <Badge variant="secondary" className="px-2">
                  Recommended
                </Badge>
              ) : (
                <ArrowRight weight="regular" className="size-4 mt-1.5 mr-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

interface MockPromptProps {
  prompt: string;
  offerings: Offering[];
  settings: IAgentSettings;
  onSelectPrompt: (prompt: string, settings: IAgentSettings) => void;
}

function MockPrompt({ prompt, offerings, settings, onSelectPrompt }: MockPromptProps) {
  const mock = useMemo(() => {
    return processPromptWithProduct(prompt, offerings);
  }, [prompt, offerings]);

  return (
    <button
      onClick={() => onSelectPrompt(mock, settings)}
      className="group p-5 inline-flex justify-between gap-4 lg:gap-8 text-left bg-card transition-colors hover:bg-accent focus-within:bg-accent outline-none"
    >
      <Prose className="prose-p:line-clamp-1 prose-sm">
        <ReactMarkdown remarkPlugins={[RemarkGFM]} rehypePlugins={[[RehypeExternalLinks, { target: "_blank", rel: "noopener noreferrer" }]]}>
          {mock}
        </ReactMarkdown>
      </Prose>
      <div className="bg-accent size-6 grid place-items-center group-hover:bg-secondary">
        <ArrowElbowDownLeft weight="regular" className="size-4" />
      </div>
    </button>
  );
}

function RecentChats() {
  const mobile = useMobileMediaQuery();

  const { data: chats = [] } = useQuery({
    queryKey: ChatApiFactory.Keys.FetchChatSessions({ page: 1, size: 10 }),
    queryFn: () => ChatApiFactory.Apis.FetchChatSessions({ page: 1, size: 10 }),
    select: (chats) => (!mobile ? chats.items.slice(0, 3) : chats.items),
  });

  if (!chats.length) {
    return null;
  }

  return (
    <div className="w-full mt-16 @container">
      <PageHeader title="Recent chats" description="Explore the latest chats you've had with our agents" className="sticky -top-7 z-40 lg:top-unset lg:static lg:z-0 bg-background pb-4 mb-2" />
      <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 px-px lg:px-0">
        {chats.map((chat) => (
          <ChatCard key={chat.id} chat={chat} responsive={true} />
        ))}
        <Link
          to="/dashboard/chats"
          className={cn("w-full h-full border bg-accent flex gap-2 items-center justify-center text-muted-foreground", mobile ? "flex-row min-h-14 rounded-xl" : "flex-col min-h-80 rounded-2xl")}
        >
          <Plus weight="bold" className="size-5" />
          <span className="text-sm font-medium">View all</span>
        </Link>
      </div>
    </div>
  );
}

function CommunityFeed() {
  const container$ = useRef<HTMLDivElement>(null!);

  return (
    <div className="w-full mt-16 space-y-6" ref={container$}>
      <PageHeader
        title="From the community"
        description="Explore the latest videos and images shared by our community"
        className="sticky -top-7 z-40 lg:top-unset lg:static lg:z-0 bg-background pb-4 mb-2"
      />
      <div className="relative px-px lg:px-0">
        <ResponsiveMasonry columnsCountBreakPoints={mColumnBreakpoints} gutterBreakPoints={mGutterBreakpoints}>
          <Masonry>
            {mCommunityCreatives.map((creative, index) => {
              return <CommunityCard key={index} url={creative.url} type={creative.type} />;
            })}
          </Masonry>
        </ResponsiveMasonry>
      </div>
    </div>
  );
}
